import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConfigurationTab } from "@presentation/hooks/useConfigurationTab";
import { Button } from "@progress/kendo-react-buttons";
import { Card, TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Grid, GridColumn, GridCustomCellProps } from "@progress/kendo-react-grid";
import { useInjection } from "@presentation/components/dependencyInjection";
import { ProjectManagementService } from "@application/useCases/workspace/ProjectManagementService";
import { arrowsMoveIcon, gridIcon, pencilIcon, plusOutlineIcon, trashIcon } from "@progress/kendo-svg-icons";
import { useTeamPermission, useProjectPermission } from "@presentation/components/helpers/usePermissionCheck";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { AssignProjectRoleDialog } from "@presentation/components/team/AssignProjectRoleDialog";
import { RemoveProjectMemberDialog } from "@presentation/components/project/RemoveProjectMemberDialog";
import { RenameProjectDialog } from "@presentation/components/project/RenameProjectDialog";
import { MoveProjectDialog } from "@presentation/components/project/MoveProjectDialog";
import { StorageConfigurationSection } from "@presentation/components/storageConfiguration/StorageConfigurationSection";
import { GatedActionButton } from "@presentation/components/permission/GatedActionButton";
import { EntitlementLockedBadge } from "@presentation/components/permission/EntitlementLockedBadge";
import { SoftwareVersionConfigurationSection } from "@presentation/components/softwareVersions/SoftwareVersionConfigurationSection";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { formatUserDisplayName, useUserDisplayNames } from "@presentation/hooks/useUserDisplayNames";
import {
  fillCardStyle,
  mutedTextStyle,
  pageFillContentStyle,
  pageHeaderRegionStyle,
  pageHeaderStyle,
  pageScrollableContentStyle,
  pageShellStyle,
  pageTitleStyle,
} from "@presentation/common/pageLayout";
import { ProjectActions, ProjectRoles, TeamActions } from "@domain/schemas/workspace";
import type { ProjectDetail, ProjectMembership, ProjectRoleName } from "@domain/schemas/workspace";
import { useIsEntitled } from "@presentation/hooks/useEntitlements";
import { EntitlementReadOnlyNotice } from "@presentation/components/permission/EntitlementReadOnlyNotice";
import { CONTEXT_MANAGEMENT_ENTITLEMENT_NAME } from "@domain/valueObjects/contextManagementEntitlement";

// Highest-privilege role first, matching RoleActionMatrix's permission ordering (Manager > Operator > Reader).
// Exported so a test can assert every `ProjectRoleName` (see `PROJECT_ROLE_NAMES`) has a rank here.
export const PROJECT_ROLE_RANK: Record<ProjectRoleName, number> = { [ProjectRoles.Manager]: 0, [ProjectRoles.Operator]: 1, [ProjectRoles.Reader]: 2 };

type AssignRoleTarget = { mode: "add" } | { mode: "change"; member: ProjectMembership };

type DialogState = { kind: "none" } | { kind: "assignRole"; target: AssignRoleTarget } | { kind: "removeMember"; userId: string } | { kind: "move" } | { kind: "rename" };

interface ProjectManagementPanelProps {
  projectId: string;
}

/**
Project settings - detail, membership, rename, and the propose-move action.
The Settings URL owns project selection; the loaded detail supplies its authoritative owning team.
*/
export const ProjectManagementPanel: React.FC<ProjectManagementPanelProps> = ({ projectId }) => {
  const projectManagementService = useInjection(ProjectManagementService);
  const navigate = useNavigate();
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState>({ kind: "none" });
  const [moveRequestNotice, setMoveRequestNotice] = useState<string | null>(null);
  const contextManagementEntitled = useIsEntitled(CONTEXT_MANAGEMENT_ENTITLEMENT_NAME);

  const canView = useProjectPermission(projectId, ProjectActions.View).allowed;
  // Every mutating project action can be an entitlement gap on a confirmed non-default project (WP8).
  const manageMembershipPermission = useProjectPermission(projectId, ProjectActions.ManageMembership, projectDetail?.isDefault);
  const renameProjectPermission = useProjectPermission(projectId, ProjectActions.Rename, projectDetail?.isDefault);
  const moveProjectPermission = useTeamPermission(projectDetail?.teamId ?? null, TeamActions.DeleteProject, projectDetail?.isDefault);
  const storageConfigurationPermission = useProjectPermission(projectId, ProjectActions.ManageStorageConfiguration, projectDetail?.isDefault);
  const softwareVersionsPermission = useProjectPermission(projectId, ProjectActions.ManageSoftwareVersions, projectDetail?.isDefault);
  const triggerJobPermission = useProjectPermission(projectId, ProjectActions.TriggerJob, projectDetail?.isDefault);
  const canManageStorageConfiguration = storageConfigurationPermission.allowed;
  const canManageSoftwareVersions = softwareVersionsPermission.allowed;
  const isReadOnlyDueToEntitlement = projectDetail?.isDefault === false && contextManagementEntitled === false;
  const recoverFromStaleProject = useWorkspaceContextStore((state) => state.recoverFromStaleProject);
  const setActiveTeamContext = useWorkspaceContextStore((state) => state.setActiveTeamContext);
  const activeTeamId = useWorkspaceContextStore((state) => state.activeTeamId);
  const activeProjectId = useWorkspaceContextStore((state) => state.activeProjectId);
  const userNames = useUserDisplayNames(React.useMemo(() => projectDetail?.memberships.map((membership) => membership.userId) ?? [], [projectDetail]));
  const sortedMemberships = React.useMemo(() => {
    const memberships = projectDetail?.memberships ?? [];
    return memberships.toSorted((left, right) => {
      const rankDifference = PROJECT_ROLE_RANK[left.role] - PROJECT_ROLE_RANK[right.role];
      return rankDifference === 0 ? formatUserDisplayName(userNames[left.userId], left.userId).localeCompare(formatUserDisplayName(userNames[right.userId], right.userId)) : rankDifference;
    });
  }, [projectDetail, userNames]);
  const [memberSearchTerm, setMemberSearchTerm] = useState("");
  const filteredMemberships = React.useMemo(() => {
    const normalizedTerm = memberSearchTerm.trim().toLowerCase();
    return normalizedTerm ? sortedMemberships.filter((membership) => formatUserDisplayName(userNames[membership.userId], membership.userId).toLowerCase().includes(normalizedTerm)) : sortedMemberships;
  }, [sortedMemberships, memberSearchTerm, userNames]);
  const [selectedTab, setSelectedTab] = useConfigurationTab(["members", "storage", "software-versions"]);

  const reloadProject = useCallback(async () => {
    try {
      const detail = await projectManagementService.getProjectAsync(projectId);
      setProjectDetail(detail);
      setLoadError(null);
    } catch (error) {
      const { status } = extractProblemDetails(error);
      // A denied or not-found response for the active project means it was deleted or access was
      // revoked mid-session (WP6.5) - recover instead of leaving the user stuck on a dead context.
      if (status === 403 || status === 404) {
        setProjectDetail(null);
        setLoadError("This project is no longer available or you no longer have access.");
        recoverFromStaleProject(projectId);
        return;
      }
      setProjectDetail(null);
      setLoadError(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not load this project.");
    }
  }, [projectId, projectManagementService, recoverFromStaleProject]);

  useEffect(() => {
    if (!projectDetail) return;
    if (activeTeamId !== projectDetail.teamId || activeProjectId !== projectDetail.id) {
      setActiveTeamContext(projectDetail.teamId, projectDetail.id);
    }
  }, [activeTeamId, activeProjectId, projectDetail, setActiveTeamContext]);

  useEffect(() => {
    if (!canView) {
      setProjectDetail(null);
      return;
    }
    void reloadProject();
  }, [canView, reloadProject]);

  // Safe to assert: the Rename button only renders once `projectDetail` is already loaded.
  const renameProject = (newName: string) => projectManagementService.renameProjectAsync(projectDetail!.id, newName);

  // eslint-disable-next-line unicorn/no-declarations-before-early-exit -- hooks above must run before the early returns below
  const handleCreateJob = () => {
    setActiveTeamContext(projectDetail!.teamId, projectDetail!.id);
    navigate("/job-setup");
  };

  if (!canView) return null;
  if (loadError)
    return (
      <div style={pageShellStyle}>
        <div style={pageScrollableContentStyle}>
          <p style={{ color: "#7f1d1d" }}>{loadError}</p>
        </div>
      </div>
    );
  if (!projectDetail) return null;

  return (
    <div style={pageShellStyle}>
      <div style={pageHeaderRegionStyle}>
        <div style={pageHeaderStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <h1 style={pageTitleStyle}>Project settings - {projectDetail.name}</h1>
            {isReadOnlyDueToEntitlement && <EntitlementLockedBadge />}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              type="button"
              fillMode="flat"
              svgIcon={gridIcon}
              title="View jobs"
              aria-label="View jobs"
              onClick={() => navigate(`/jobs?teamId=${projectDetail.teamId}&projectId=${projectDetail.id}`)}
            />
            <GatedActionButton type="button" fillMode="flat" svgIcon={plusOutlineIcon} title="Create job" aria-label="Create job" permission={triggerJobPermission} onClick={handleCreateJob} />
            <GatedActionButton
              type="button"
              fillMode="flat"
              svgIcon={pencilIcon}
              title="Rename project"
              aria-label="Rename project"
              permission={renameProjectPermission}
              onClick={() => setDialog({ kind: "rename" })}
            />
            <GatedActionButton
              type="button"
              fillMode="flat"
              svgIcon={arrowsMoveIcon}
              title="Move project"
              aria-label="Move project"
              permission={moveProjectPermission}
              onClick={() => setDialog({ kind: "move" })}
            />
          </div>
        </div>

        {moveRequestNotice && <p style={{ color: "#166534" }}>{moveRequestNotice}</p>}
      </div>

      {isReadOnlyDueToEntitlement && <EntitlementReadOnlyNotice />}

      <div style={pageFillContentStyle}>
        <Card style={fillCardStyle}>
          <TabStrip className="scrollable-tabstrip" selected={selectedTab} onSelect={(event) => setSelectedTab(event.selected)}>
            <TabStripTab title="Members">
              <div style={{ display: "grid", gap: 10, padding: "4px 0 12px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "0 1rem" }}>
                  <input
                    type="search"
                    className="project-sidebar-search"
                    placeholder="Search members"
                    value={memberSearchTerm}
                    onChange={(event) => setMemberSearchTerm(event.target.value)}
                    style={{ minWidth: 220 }}
                  />
                  <GatedActionButton type="button" permission={manageMembershipPermission} onClick={() => setDialog({ kind: "assignRole", target: { mode: "add" } })}>
                    Add member
                  </GatedActionButton>
                </div>
                <Grid data={filteredMemberships} dataItemKey="id">
                  <GridColumn
                    field="userId"
                    title="Name"
                    cell={(properties: GridCustomCellProps) => <td>{formatUserDisplayName(userNames[properties.dataItem.userId], properties.dataItem.userId)}</td>}
                  />
                  <GridColumn field="role" title="Role" width="160px" />
                  <GridColumn
                    title=""
                    width="90px"
                    cell={(properties: GridCustomCellProps) => (
                      <td>
                        <div style={{ display: "flex", gap: 8 }}>
                          <GatedActionButton
                            type="button"
                            fillMode="flat"
                            svgIcon={pencilIcon}
                            title="Change role"
                            aria-label="Change role"
                            permission={manageMembershipPermission}
                            onClick={() => setDialog({ kind: "assignRole", target: { mode: "change", member: properties.dataItem } })}
                          />
                          <GatedActionButton
                            type="button"
                            fillMode="flat"
                            themeColor="error"
                            svgIcon={trashIcon}
                            title="Remove"
                            aria-label="Remove"
                            permission={manageMembershipPermission}
                            onClick={() => setDialog({ kind: "removeMember", userId: properties.dataItem.userId })}
                          />
                        </div>
                      </td>
                    )}
                  />
                </Grid>
              </div>
            </TabStripTab>

            <TabStripTab title="Storage configuration">
              <div style={{ padding: "4px 4px 12px" }}>
                <StorageConfigurationSection level="project" contextId={projectDetail.id} canManage={canManageStorageConfiguration} />
              </div>
            </TabStripTab>

            <TabStripTab title="Software versions">
              <div style={{ display: "grid", gap: 12, padding: "4px 4px 12px" }}>
                <p style={mutedTextStyle}>
                  {canManageSoftwareVersions
                    ? "Allowed and default software versions for this project. Clearing all overrides falls back to the team's/deployment's defaults."
                    : "Resolved from the deployment, team, and project software-version configuration."}
                </p>
                <SoftwareVersionConfigurationSection level="project" contextId={projectId} canManage={canManageSoftwareVersions} />
              </div>
            </TabStripTab>
          </TabStrip>
        </Card>
      </div>

      {dialog.kind === "assignRole" && (
        <AssignProjectRoleDialog
          projectId={projectDetail.id}
          projectName={projectDetail.name}
          existingMember={dialog.target.mode === "change" ? { userId: dialog.target.member.userId, role: dialog.target.member.role } : undefined}
          existingMemberUserIds={projectDetail.memberships.map((membership) => membership.userId)}
          onClose={() => setDialog({ kind: "none" })}
          onSuccess={setProjectDetail}
        />
      )}
      {dialog.kind === "removeMember" && <RemoveProjectMemberDialog projectId={projectDetail.id} userId={dialog.userId} onClose={() => setDialog({ kind: "none" })} onSuccess={setProjectDetail} />}
      {dialog.kind === "move" && (
        <MoveProjectDialog
          sourceTeamId={projectDetail.teamId}
          projectId={projectDetail.id}
          projectName={projectDetail.name}
          onClose={() => setDialog({ kind: "none" })}
          onSuccess={() => setMoveRequestNotice("Move proposed. The target team's Managers must accept it before the project moves.")}
        />
      )}
      {dialog.kind === "rename" && <RenameProjectDialog currentName={projectDetail.name} onRename={renameProject} onClose={() => setDialog({ kind: "none" })} onSuccess={setProjectDetail} />}
    </div>
  );
};

export default ProjectManagementPanel;
